/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { lazy, Suspense } from "react";
import { RefSectionsEntitiesFragment, RefSectionsFragment } from "@cosmos/hygraph-schema";

const AccordionFeatureCardsSection = lazy(() =>
	import("./accordion-feature-card/AccordionFeatureCards.section").then((m) => ({
		default: m.AccordionFeatureCardsSection,
	})),
);
const AccordionTierCardsSection = lazy(() =>
	import("./accordion-tier-cards/AccordionTierCards.section").then((m) => ({
		default: m.AccordionTierCardsSection,
	})),
);
const AriesCardsSection = lazy(() =>
	import("./aries-cards/AriesCards.section").then((m) => ({ default: m.AriesCardsSection })),
);
const AssetCarouselSection = lazy(() =>
	import("./asset-carousel/AssetCarousel.section").then((m) => ({ default: m.AssetCarouselSection })),
);
const AssetGallerySection = lazy(() =>
	import("./asset-gallery/AssetGallery.section").then((m) => ({ default: m.AssetGallerySection })),
);
const AssetSection = lazy(() => import("./asset/AssetSection").then((m) => ({ default: m.AssetSection })));
const BlogCardsSection = lazy(() =>
	import("./blog-cards/BlogCards.section").then((m) => ({ default: m.BlogCardsSection })),
);
const CkRichTextSection = lazy(() =>
	import("./rich-text/CkRichText.section").then((m) => ({ default: m.CkRichTextSection })),
);
const CommunitySection = lazy(() =>
	import("./community/Community.section").then((m) => ({ default: m.CommunitySection })),
);
const ComplexCtaCardsSection = lazy(() =>
	import("./complex-cta-cards/ComplexCtaCards.section").then((m) => ({ default: m.ComplexCtaCardsSection })),
);
const ContentLargeImageSection = lazy(() =>
	import("./content-large-image/ContentLargeImage.section").then((m) => ({ default: m.ContentLargeImageSection })),
);
const ContentSliderAssetSection = lazy(() =>
	import("./content-slider-asset/ContentSliderAsset.section").then((m) => ({ default: m.ContentSliderAssetSection })),
);
const CruxSection = lazy(() => import("./crux/Crux.section").then((m) => ({ default: m.CruxSection })));
const DelphinusCardsGridSection = lazy(() =>
	import("./delphinus-cards/DelphinusCardsGrid.section").then((m) => ({ default: m.DelphinusCardsGridSection })),
);
const DividerSection = lazy(() => import("./divider/Divider.section").then((m) => ({ default: m.DividerSection })));
const DownloadCardsSection = lazy(() =>
	import("./download-cards/DownloadCards.section").then((m) => ({ default: m.DownloadCardsSection })),
);
const DownloadableSection = lazy(() =>
	import("./downloadable/Downloadable.section").then((m) => ({ default: m.DownloadableSection })),
);
const EmbedSection = lazy(() => import("./embed/Embed.section").then((m) => ({ default: m.EmbedSection })));
const EventsTableSection = lazy(() =>
	import("./events-table/EventsTable.section").then((m) => ({ default: m.EventsTableSection })),
);
const FAQSearchSection = lazy(() =>
	import("./faq-search/FAQ-Search.section").then((m) => ({ default: m.FAQSearchSection })),
);
const FAQSection = lazy(() => import("./faq/FAQ.section").then((m) => ({ default: m.FAQSection })));
const FeatureCardsSection = lazy(() =>
	import("./feature-cards/FeatureCards.section").then((m) => ({ default: m.FeatureCardsSection })),
);
const FeaturePanelsSection = lazy(() =>
	import("./feature-panels/FeaturePanels.section").then((m) => ({ default: m.FeaturePanelsSection })),
);
const GeminiCardsSection = lazy(() =>
	import("./gemini-cards/GeminiCards.section").then((m) => ({ default: m.GeminiCardsSection })),
);
const HydrusColumnListSection = lazy(() =>
	import("./hydrus-column-list/HydrusColumnList.section").then((m) => ({ default: m.HydrusColumnListSection })),
);
const LatestNewsSection = lazy(() =>
	import("./latest-news/LatestNews.section").then((m) => ({ default: m.LatestNewsSection })),
);
const LearnSection = lazy(() => import("./learn-cards/Learn.section").then((m) => ({ default: m.LearnSection })));
const LicensesCollapsedSection = lazy(() =>
	import("./licenses-collapsed/LicensesCollapsed.section").then((m) => ({ default: m.LicensesCollapsedSection })),
);
const LogoListSection = lazy(() =>
	import("./logo-list/LogoList.section").then((m) => ({ default: m.LogoListSection })),
);
const LogoMultiCardsSection = lazy(() =>
	import("./logo-multi-cards/LogoMultiCards.section").then((m) => ({ default: m.LogoMultiCardsSection })),
);
const NewsCardsSection = lazy(() =>
	import("./news-cards/NewsCards.section").then((m) => ({ default: m.NewsCardsSection })),
);
const NewsMediaCardsSection = lazy(() =>
	import("./news-media-cards/NewsMediaCards.section").then((m) => ({ default: m.NewsMediaCardsSection })),
);
const NewsletterSignup = lazy(() =>
	import("./newsletter-signup/NewsletterSignup.section").then((m) => ({ default: m.NewsletterSignup })),
);
const NumberCalloutSection = lazy(() =>
	import("./number-callout/NumberCallout.section").then((m) => ({ default: m.NumberCalloutSection })),
);
const QuoteSection = lazy(() => import("./quote/Quote.section").then((m) => ({ default: m.QuoteSection })));
const ResellersSearchSection = lazy(() =>
	import("./resellers-search/ResellersSearch.section").then((m) => ({ default: m.ResellersSearchSection })),
);
const RichTextSection = lazy(() =>
	import("./rich-text/RichText.section").then((m) => ({ default: m.RichTextSection })),
);
const SimpleCtaSection = lazy(() =>
	import("./simple-cta/SimpleCta.section").then((m) => ({ default: m.SimpleCtaSection })),
);
const SlimContactCardsSection = lazy(() =>
	import("./slim-contact-cards/SlimContactCards.section").then((m) => ({ default: m.SlimContactCardsSection })),
);
const SpeakerCardsSection = lazy(() =>
	import("./speaker-cards/SpeakerCards.section").then((m) => ({ default: m.SpeakerCardsSection })),
);
const TabbedBlogCardsSection = lazy(() =>
	import("./tabbed-blog-cards/TabbedBlogCards.section").then((m) => ({ default: m.TabbedBlogCardsSection })),
);
const TabbedContentImageSection = lazy(() =>
	import("./tabbed-content-image/TabbedContentImage.section").then((m) => ({
		default: m.TabbedContentImageSection,
	})),
);
const TabbedFullWidthAssetSection = lazy(() =>
	import("./tabbed-full-width-asset/TabbedFullWidthAsset.section").then((m) => ({
		default: m.TabbedFullWidthAssetSection,
	})),
);
const UTCardsSection = lazy(() => import("./ut-cards/UTCards.section").then((m) => ({ default: m.UTCardsSection })));
const BlobTableSection = lazy(() =>
	import("./blob-table/BlobTable.section").then((m) => ({ default: m.BlobTableSection })),
);
const DevelopersListSection = lazy(() =>
	import("./developers-list/DevelopersList.section").then((m) => ({ default: m.DevelopersListSection })),
);
const PluginListTableSection = lazy(() =>
	import("./plugin-list-table/PluginListTable.section").then((m) => ({ default: m.PluginListTableSection })),
);

type SectionType<Section extends { __typename: any }> = {
	[S in Section as S["__typename"]]: any;
};

type RefSectionsEntitiesSection = Exclude<RefSectionsEntitiesFragment["section"], null | undefined>;

export type SectionTypename = keyof SectionType<RefSectionsEntitiesSection>;

export const getSections = (
	sections?: RefSectionsFragment[] | null,
	extraProps?: (__typename: SectionTypename, idx: number) => any,
): (React.JSX.Element | null | undefined)[] => {
	if (!sections || !sections.length) return [];
	return sections.map((refSection, i) => {
		const section = refSection.section;
		if (!section || !section.__typename) return;

		switch (section.__typename) {
			case "CosmosSectionAsset":
				return (
					<Suspense key={`ref-section-asset-${i}`}>
						<AssetSection {...section} />
					</Suspense>
				);
			case "CosmosSectionAssetCarousel":
				return (
					<Suspense key={`ref-section-asset-carousel-${i}`}>
						<AssetCarouselSection {...section} />
					</Suspense>
				);
			case "CosmosSectionAssetGallery":
				return (
					<Suspense key={`ref-section-asset-gallery-${i}`}>
						<AssetGallerySection {...section} />
					</Suspense>
				);
			case "CosmosSectionBlogCard":
				return (
					<Suspense key={`ref-section-blog-card-${i}`}>
						<BlogCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionCommunity":
				return (
					<Suspense key={`ref-section-community-${i}`}>
						<CommunitySection {...section} />
					</Suspense>
				);
			case "CosmosSectionComplexCtaCard":
				return (
					<Suspense key={`ref-section-complex-cta-card-${i}`}>
						<ComplexCtaCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionContentLargeImage": {
				const otherProps = extraProps ? extraProps("CosmosSectionContentLargeImage", i) : {};
				return (
					<Suspense key={`ref-section-content-large-image-${i}`}>
						<ContentLargeImageSection {...section} sectionIndex={i} {...otherProps} />
					</Suspense>
				);
			}
			case "CosmosSectionCrux":
				return (
					<Suspense key={`ref-section-crux-${i}`}>
						<CruxSection {...section} />
					</Suspense>
				);
			case "CosmosSectionDelphinusCardsGrid":
				return (
					<Suspense key={`ref-section-delphinus-cards-grid-${i}`}>
						<DelphinusCardsGridSection {...section} />
					</Suspense>
				);
			case "CosmosSectionDivider":
				return (
					<Suspense key={`ref-section-divider-${i}`}>
						<DividerSection {...section} />
					</Suspense>
				);
			case "CosmosSectionDownloadable":
				return (
					<Suspense key={`ref-section-downloadable-${i}`}>
						<DownloadableSection {...section} />
					</Suspense>
				);
			case "CosmosSectionDownloadCard":
				return (
					<Suspense key={`ref-section-download-card-${i}`}>
						<DownloadCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionEmbed":
				return (
					<Suspense key={`ref-section-embed-${i}`}>
						<EmbedSection {...section} />
					</Suspense>
				);
			case "CosmosSectionEventsTable":
				return (
					<Suspense key={`ref-section-events-table-${i}`}>
						<EventsTableSection {...section} />
					</Suspense>
				);
			case "CosmosSectionFeatureCard":
				return (
					<Suspense key={`ref-section-feature-card-${i}`}>
						<FeatureCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionFaq":
				return (
					<Suspense key={`ref-section-faqs-${i}`}>
						<FAQSection {...section} />
					</Suspense>
				);
			case "CosmosSectionGameDevFeatureCard":
				return (
					<Suspense key={`ref-section-feature-panels-${i}`}>
						<FeaturePanelsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionGeminiCard":
				return (
					<Suspense key={`ref-section-gemini-cards-${i}`}>
						<GeminiCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionHydrusColumnList":
				return (
					<Suspense key={`ref-section-hydrus-column-list-${i}`}>
						<HydrusColumnListSection {...section} />
					</Suspense>
				);
			case "CosmosSectionLatestNews":
				return (
					<Suspense key={`ref-section-learn-${i}`}>
						<LatestNewsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionLearn":
				return (
					<Suspense key={`ref-section-learn-${i}`}>
						<LearnSection {...section} />
					</Suspense>
				);
			case "CosmosSectionLogoList":
				return (
					<Suspense key={`ref-section-logo-list-${i}`}>
						<LogoListSection {...section} />
					</Suspense>
				);
			case "CosmosSectionLogoMultiCard":
				return (
					<Suspense key={`logo-multi-card-${i}`}>
						<LogoMultiCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionNewsCard":
				return (
					<Suspense key={`ref-section-news-card-${i}`}>
						<NewsCardsSection {...section} sectionIndex={i} />
					</Suspense>
				);
			case "CosmosSectionNewsMediaCard":
				return (
					<Suspense key={`ref-section-news-media-card-${i}`}>
						<NewsMediaCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionNumberCallout":
				return (
					<Suspense key={`ref-section-number-callout-${i}`}>
						<NumberCalloutSection {...section} sectionIndex={i} />
					</Suspense>
				);
			case "CosmosSectionLicensesCollapsed":
				return (
					<Suspense key={`ref-section-licenses-collapsed-${i}`}>
						<LicensesCollapsedSection {...section} />
					</Suspense>
				);
			case "CosmosSectionAccordionFeatureCard":
				return (
					<Suspense key={`ref-section-accordion-featurecards-${i}`}>
						<AccordionFeatureCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionAccordionTierCards":
				return (
					<Suspense key={`ref-section-accordion-tier-cards-${i}`}>
						<AccordionTierCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionRichText":
				return (
					<Suspense key={`ref-section-richtext-${i}`}>
						<RichTextSection {...section} />
					</Suspense>
				);
			case "CosmosSectionNewsletterSignup":
				return (
					<Suspense key={`ref-section-newslettersignup-${i}`}>
						<NewsletterSignup {...section} />
					</Suspense>
				);
			case "CosmosSectionSimpleCta":
				return (
					<Suspense key={`ref-section-simplecta-${i}`}>
						<SimpleCtaSection {...section} />
					</Suspense>
				);
			case "CosmosSectionSlimContactCard":
				return (
					<Suspense key={`ref-section-slim-contact-cards-${i}`}>
						<SlimContactCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionSpeakerCard":
				return (
					<Suspense key={`ref-section-speaker-cards-${i}`}>
						<SpeakerCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionTabbedFullWidthAsset":
				return (
					<Suspense key={`ref-section-tabbed-full-width-asset-${i}`}>
						<TabbedFullWidthAssetSection {...section} />
					</Suspense>
				);
			case "CosmosSectionTabbedBlogCard":
				return (
					<Suspense key={`ref-section-tabbed-blog-cards-${i}`}>
						<TabbedBlogCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionTabbedContentImage":
				return (
					<Suspense key={`ref-section-tabbed-content-image-${i}`}>
						<TabbedContentImageSection {...section} />
					</Suspense>
				);
			case "CosmosSectionFaqSearch":
				return (
					<Suspense key={`ref-section-faq-search-${i}`}>
						<FAQSearchSection {...section} />
					</Suspense>
				);
			case "CosmosSectionCkRichText":
				return (
					<Suspense key={`ref-section-ckrichtext-${i}`}>
						<CkRichTextSection {...section} />
					</Suspense>
				);
			case "CosmosSectionAriesCards":
				return (
					<Suspense key={`ref-section-aries-cards-${i}`}>
						<AriesCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionContentSliderAsset": {
				const otherProps = extraProps ? extraProps("CosmosSectionContentSliderAsset", i) : {};
				return (
					<Suspense key={`ref-section-content-slider-asset-${i}`}>
						<ContentSliderAssetSection {...section} sectionIndex={i} {...otherProps} />
					</Suspense>
				);
			}
			case "CosmosSectionQuote":
				return (
					<Suspense key={`ref-section-quote-${i}`}>
						<QuoteSection {...section} />
					</Suspense>
				);
			case "CosmosSectionUtCard":
				return (
					<Suspense key={`ref-section-ut-cards-${i}`}>
						<UTCardsSection {...section} />
					</Suspense>
				);
			case "CosmosSectionResellersSearch":
				return (
					<Suspense key={`ref-section-resellers-search-${i}`}>
						<ResellersSearchSection {...section} />
					</Suspense>
				);
			case "CosmosSectionBlobTable":
				return (
					<Suspense key={`ref-section-blob-table-${i}`}>
						<BlobTableSection {...section} />
					</Suspense>
				);
			case "CosmosSectionDevelopersList":
				return (
					<Suspense key={`ref-section-developers-list-${i}`}>
						<DevelopersListSection {...section} />
					</Suspense>
				);
			case "CosmosSectionPluginListTable":
				return (
					<Suspense key={`ref-section-plugin-list-table-${i}`}>
						<PluginListTableSection {...section} />
					</Suspense>
				);
			default:
				return null;
		}
	});
};
